<script setup lang="ts">
import { defineAsyncComponent, inject, nextTick, onMounted } from 'vue';

import PageHeader from '@/components/common/PageHeader.vue';
import TheContainer from '@/components/common/TheContainer.vue';
import { useUtilsStore } from '@/stores/utils';
import { useAuthStore } from '@/stores/auth';
import { useScreenTracker } from '@/composables/common/useScreenTracker';
import { storeToRefs } from 'pinia';
import { useToast } from 'vue-toastification';
import router from '@/router/router.ts';
import type { Notifier } from '@airbrake/browser';
import type { OverridedMixpanel } from 'mixpanel-browser';

// コンポーネントを非同期で読み込むことで、初期バンドルサイズを削減し、初回表示を高速化。
const TheHeader = defineAsyncComponent(() => import('@/components/common/TheHeader.vue'));
const SPFooter = defineAsyncComponent(() => import('./components/sp/SPFooter.vue'));
const TheLoading = defineAsyncComponent(() => import('@/components/common/TheLoading.vue'));
const airbrake = inject('airbrake') as Notifier | null;
const mixpanel = inject('mixpanel') as OverridedMixpanel | null;

const utilsStore = useUtilsStore();
const authStore = useAuthStore();
const { isLoggedIn } = storeToRefs(authStore);
useScreenTracker();
onMounted(async () => {
  await router.isReady();
  const toast = useToast();
  // セッション確認（失敗してもアプリは起動する）
  try {
    await authStore.session();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    toast.error('セッション取得に失敗しました', error?.message);
  }
  await nextTick();
  if (isLoggedIn && authStore.user?.id) {
    airbrake?.addFilter((notice) => {
      notice.session = {
        username: authStore.user?.username
      };

      notice.context.version = __APP_VERSION__;
      return notice;
    });
    mixpanel?.identify(authStore.user?.username);
    mixpanel?.people.set_once({
      $first_name: authStore.user?.first_name,
      $last_name: authStore.user?.last_name,
      $email: authStore.user?.email,
      team: authStore.user?.team.name
    });
    if (router.currentRoute.value.path === '/') {
      await router.replace('/matchlist');
    }
  } else {
    await router.replace('/login');
  }
});
</script>

<template>
  <div class="app-container">
    <the-header v-if="isLoggedIn" />
    <PageHeader v-if="isLoggedIn" />
    <main class="main-height">
      <router-view v-slot="{ Component, route }">
        <suspense>
          <template #default>
            <transition :name="String(route.meta.transition) || 'slide-right'" mode="out-in">
              <the-container :key="route.path">
                <component :is="Component" />
              </the-container>
            </transition>
          </template>
          <template #fallback>
            <the-loading />
          </template>
        </suspense>
      </router-view>
    </main>
    <the-loading v-if="utilsStore.isLoading" />
    <SPFooter />
  </div>
</template>

<style lang="scss">
// ブラウザのデフォルトマージンや、意図しないスクロールバーによる余白を防ぐためのグローバルリセット
html,
body,
#app {
  height: 100%;
  margin: 0;
  padding: 0;

  /* SafariとChromeでのfont-weightの違いを吸収し、テキストレンダリングを滑らかにする */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.app-container {
  display: flex;
  flex-direction: column;
  height: 100svh;
  position: relative;

  // コンテナ自体がスクロールしないように設定
  overflow: hidden;
}

.main-height {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  padding-top: v.$pc-total-header-height;

  // コンテンツが溢れた場合に、この要素内でスクロールさせる
  width: 100vw;
  overflow-y: scroll;
  -ms-overflow-style: none; /* IE, Edge */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  @include m.sp_mode {
    padding-top: v.$sp-total-header-height;
  }

  &:has(.single-play-video-page) {
    padding-top: calc(v.$pc-total-header-height - 2rem);
    @include m.sp_mode {
      padding-top: calc(v.$sp-total-header-height - 20px);
    }
  }

  &:has(.login-page) {
    padding-top: unset;
    background-color: v.$basic-black;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* 右からスライドインするアニメーション */
.slide-right-enter-active {
  transition: all 0.4s ease-out;
}

.slide-right-leave-active {
  transition: all 0.2s ease-in;
}

.slide-right-enter-from {
  transform: translateX(20px);
  opacity: 0;
}

.slide-right-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

/* アニメーションの持続時間とタイミング設定 */
.animate__animated {
  animation-duration: 0.8s;
  animation-fill-mode: both;
}
</style>
